import type { CSSProperties, ReactElement } from 'react';
import { useClickSound } from '../hooks/useClickSound';

type TabId = 'globe' | 'superteams' | 'projects' | 'events' | 'profile';

interface BottomNavProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  isAuthenticated: boolean;
}

export default function BottomNav({ activeTab, onTabChange }: BottomNavProps) {
  const { withClickSound } = useClickSound();
  const isMobile = window.innerWidth < 768;

  if (!isMobile) return null;

  const navStyle: CSSProperties = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
    paddingTop: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
  };

  const navContentStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    height: '56px',
  };

  const buttonStyle = (isActive: boolean, isCenter: boolean = false): CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: isCenter ? '12px 16px' : '8px 12px',
    background: isCenter && isActive
      ? 'linear-gradient(135deg, rgba(20, 241, 149, 0.2), rgba(153, 69, 255, 0.2))'
      : 'transparent',
    border: isCenter && isActive
      ? '1px solid rgba(20, 241, 149, 0.3)'
      : 'none',
    borderRadius: isCenter ? '16px' : '0',
    cursor: 'pointer',
    flex: 1,
    maxWidth: isCenter ? '90px' : '80px',
    transition: 'all 0.2s ease',
    opacity: isActive ? 1 : 0.6,
    position: isCenter ? 'relative' : 'static',
    transform: isCenter && isActive ? 'translateY(-8px)' : 'translateY(0)',
    boxShadow: isCenter && isActive
      ? '0 4px 12px rgba(20, 241, 149, 0.3)'
      : 'none',
  });

  const iconStyle: CSSProperties = {
    width: '24px',
    height: '24px',
    color: 'currentColor',
  };

  const labelStyle = (isActive: boolean): CSSProperties => ({
    fontSize: '10px',
    fontWeight: isActive ? 600 : 500,
    color: isActive 
      ? 'transparent'
      : 'rgba(255, 255, 255, 0.7)',
    background: isActive
      ? 'linear-gradient(135deg, #14F195, #9945FF)'
      : 'transparent',
    WebkitBackgroundClip: isActive ? 'text' : 'unset',
    backgroundClip: isActive ? 'text' : 'unset',
    WebkitTextFillColor: isActive ? 'transparent' : 'unset',
  });

  const tabs: Array<{ id: TabId; label: string; icon: ReactElement; isCenter?: boolean }> = [
    {
      id: 'projects',
      label: 'Projects',
      icon: (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      id: 'globe',
      label: 'Globe',
      isCenter: true,
      icon: (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    },
    {
      id: 'superteams',
      label: 'Superteams',
      icon: (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      id: 'events',
      label: 'Events',
      icon: (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
        </svg>
      ),
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
  ];

  return (
    <nav style={navStyle}>
      <div style={navContentStyle}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const isCenter = tab.isCenter || false;
          return (
            <button
              key={tab.id}
              onClick={withClickSound(() => onTabChange(tab.id))}
              style={buttonStyle(isActive, isCenter)}
              aria-label={tab.label}
              aria-selected={isActive}
            >
              <div style={{ 
                color: isActive ? '#14F195' : 'rgba(255, 255, 255, 0.7)',
                transform: isCenter && isActive ? 'scale(1.1)' : 'scale(1)',
                transition: 'transform 0.2s ease',
              }}>
                {tab.icon}
              </div>
              {!isCenter && <span style={labelStyle(isActive)}>{tab.label}</span>}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
